import type Quill from "quill";

export const stripTrailingNewline = (s: string): string => {
    return s.endsWith("\n") ? s.slice(0, -1) : s;
};

export const getImageIndex = (q: Quill): number => {
    const ops = q.getContents().ops ?? [];
    let cursor = 0;

    for (const op of ops) {
        if (typeof op.insert === "string") {
            cursor += op.insert.length;
        } else if (op.insert && "image" in op.insert) {
            return cursor;
        }
    }

    return -1;
};

export const getCurrentImage = (q: Quill): { idx: number; src: string | null } => {
    const ops = q.getContents().ops ?? [];
    let cursor = 0;
    for (const op of ops) {
        if (typeof op.insert === "string") {
            cursor += op.insert.length;
        } else if (op.insert && "image" in op.insert) {
            return { idx: cursor, src: typeof op.insert?.image === "string" ? op.insert.image : null };
        }
    }
    return { idx: -1, src: null };
};

// Build HTML for clipboard: clone editor DOM, hard-size images for email clients.
export const buildEmailHtml = (quillRoot: HTMLElement, maxWidthPx = 600): string => {
    const clone = quillRoot.cloneNode(true) as HTMLElement;

    const origImgs = Array.from(quillRoot.querySelectorAll("img"));
    const cloneImgs = Array.from(clone.querySelectorAll("img"));

    cloneImgs.forEach((cloneImg, i) => {
        const srcImg = origImgs[i];
        if (!srcImg) {
            return;
        }

        // Use the image as displayed in the editor, not its raw bitmap size
        const displayed = Math.round(srcImg.getBoundingClientRect().width || srcImg.width || maxWidthPx);
        const natW = srcImg.naturalWidth || displayed || maxWidthPx;
        const natH = srcImg.naturalHeight || displayed;
        const targetW = Math.min(Math.max(1, displayed || natW), maxWidthPx);
        const targetH = Math.round((natH * targetW) / Math.max(1, natW));

        // Outlook: width/height attributes
        cloneImg.setAttribute("width", String(targetW));
        cloneImg.setAttribute("height", String(targetH));

        // Remove responsive hints that can override width
        cloneImg.removeAttribute("srcset");
        cloneImg.removeAttribute("sizes");

        // Inline CSS for Gmail/Webmail
        const style = cloneImg.getAttribute("style") ?? "";
        cloneImg.setAttribute(
            "style",
            [style, `width:${targetW}px`, `height:${targetH}px`, `max-width:${targetW}px`, "display:block"]
                .filter(Boolean)
                .join(";"),
        );

        // Optional: wrap to guard layout widths in some clients
        const wrapper = cloneImg.ownerDocument.createElement("div");
        wrapper.setAttribute("style", `max-width:${targetW}px;`);
        cloneImg.replaceWith(wrapper);
        wrapper.appendChild(cloneImg);
    });

    return clone.innerHTML;
};